import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ModalBaseComponent } from 'app/shared/components/base/modal-base/modal-base.component';
import { ModuleSelectionComponent } from 'app/shared/components/module-selection/module-selection.component';
import { Permissions } from 'app/shared/constants/permissions';
import { Sections } from 'app/shared/constants/sections';
import { CompanyModule } from 'app/shared/models/company-module.model';
import { Company, IbanXSPaymentRequestUiMode, SignhostProvider } from 'app/shared/models/company.model';
import { DaysOption } from 'app/shared/models/days-option.model';
import { ModuleSelection } from 'app/shared/models/module-selection.model';
import { PageStyle } from 'app/shared/models/page-style.model';
import { CompanyService } from 'app/shared/services/company.service';
import { EventService } from 'app/shared/services/event.service';
import { PageStyleService } from 'app/shared/services/page-style.service';
import { SectionService } from 'app/shared/services/section.service';
import { Utils } from 'app/shared/utils/utils';
import { format } from 'util';
import { Constants } from '../../../shared/constants/constants';
import { CustomerOrganizationBaseModel } from '../../../shared/models/customer-organization-base.model';
import { CRMService } from '../../../shared/services/crm.service';
import { Observable } from 'rxjs/Observable';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-company',
  templateUrl: './company.component.html',
  styleUrls: ['./company.component.scss']
})

export class CompanyComponent extends ModalBaseComponent implements OnInit {

  @Input() currentObject: Company;

  get hasCompanyDetailsViewPermission(): boolean {
    return this.hasPermission(Permissions.Company_Details_View);
  }

  get hasCompanyBanksConsentViewPermission(): boolean {
    return this.hasPermission(Permissions.Company_BanksConsent_View);
  }

  get openOnboardingUrlButtonDisabled(): boolean {
    return Utils.isStringNullOrWhiteSpace(this.currentObject.IbanXSOnboardingUrl);
  }

  get startIbanXSOnboardingButtonDisabled(): boolean {
    return !Utils.isStringNullOrWhiteSpace(this.currentObject.IbanXSOnboardingUrl);
  }

  /**
   * The public image URLs are keyed on the company slug, so without a saved slug there is no URL to
   * hand out. The saved slug is used rather than the edited one, because only the saved slug resolves.
   */
  get hasSlugForImageUrls(): boolean {
    return !Utils.isStringNullOrWhiteSpace(this.persistedSlug);
  }

  get logoImageUrl(): string {
    if (!this.hasSlugForImageUrls || !this.currentPageStyle.LogoImageFileName) {
      return null;
    }

    return this.pageStyleService.getLogoImageUrlByCompanySlug(this.persistedSlug);
  }

  get backgroundImageUrl(): string {
    if (!this.hasSlugForImageUrls || !this.currentPageStyle.BackgroundImageFileName) {
      return null;
    }

    return this.pageStyleService.getBackgroundImageUrlByCompanySlug(this.persistedSlug);
  }

  /**
   * The image URLs and the public payment action URLs both embed the slug, so changing an already
   * published slug breaks every template and link that still refers to the old one.
   */
  get slugChangedFromPersistedValue(): boolean {
    return !Utils.isStringNullOrWhiteSpace(this.persistedSlug) &&
      this.currentObject.Slug !== this.persistedSlug;
  }

  constructor(public companyService: CompanyService,
              protected pageStyleService: PageStyleService,
              protected activeModal: NgbActiveModal,
              protected modalService: NgbModal,
              protected translateService: TranslateService,
              protected sectionService: SectionService,
              protected eventService: EventService,
              private toastrService: ToastrService) {
    super(activeModal, sectionService, Sections.Company);
  }

  @ViewChild(ModuleSelectionComponent, {static: false}) moduleSelectionComponent;
  @ViewChild('companyDetailsLink', {static: false}) companyDetailsLink: ElementRef;
  @ViewChild('companyBanksLink', {static: false}) companyBanksLink: ElementRef;

  refreshNeeded = false;
  currentPageStyle: PageStyle;
  persistedSlug: string;

  /**
   * The slug the backend rejected as already taken, or null. Company slugs are global and chosen by hand, so
   * the backend refuses a duplicate rather than de-duplicating it - which means the administrator has to pick
   * another one, and has to be told which field to change. The toast raised by the service is not enough on
   * its own: it says nothing about where to look, and it carries the backend's English sentence.
   */
  rejectedSlug: string = null;

  /**
   * Whether the slug currently in the form is the one the backend rejected. Comparing rather than holding a
   * flag means editing the slug clears the message by itself, and re-entering the rejected value brings it
   * back without another round trip.
   */
  get slugIsNotUnique(): boolean {
    return this.rejectedSlug !== null && this.currentObject.Slug === this.rejectedSlug;
  }
  addedSuccessfullyMessage: string;
  updatedSuccessfullyMessage: string;
  onboardingUrlGeneratedSuccessfullyMessage: string;
  logoImageUploadedSuccessfullyMessage: string;
  logoImageIsDeletedMessage: string;
  backgroundImageUploadedSuccessfullyMessage: string;
  backgroundImageIsDeletedMessage: string;
  imageUrlCopiedToClipboardMessage: string;

  // One flag across both images. The page style row is created by whichever upload arrives first when a
  // company has none, and that lookup-then-insert is not serialised, so two uploads started before the
  // first returns can each insert a row. Blocking the controls while a request is in flight removes the
  // way that actually happens: clicking upload-logo and then upload-background straight after. The race
  // itself needs a unique constraint on PageStyles.CompanyId, tracked separately.
  imageRequestInFlight = false;

  showSignhostApiKey = false;
  showPaymentLinkProviderMollieApiKey = false;

  readonly SignhostProvider = SignhostProvider;
  readonly IbanXSPaymentRequestUiMode = IbanXSPaymentRequestUiMode;

  ngOnInit(): void {
    this.initializeObject();
    this.translateService.get(['company.addedSuccessfullyMessage', 'company.updatedSuccessfullyMessage', 'company.onboardingUrlGeneratedSuccessfullyMessage',
      'company.logoImageUploadedSuccessfullyMessage', 'company.logoImageIsDeletedMessage', 'company.backgroundImageUploadedSuccessfullyMessage', 'company.backgroundImageIsDeletedMessage',
      'company.imageUrlCopiedToClipboardMessage'])
      .subscribe(translations => {
        this.addedSuccessfullyMessage = translations['company.addedSuccessfullyMessage'];
        this.updatedSuccessfullyMessage = translations['company.updatedSuccessfullyMessage'];
        this.onboardingUrlGeneratedSuccessfullyMessage = translations['company.onboardingUrlGeneratedSuccessfullyMessage'];
        this.logoImageUploadedSuccessfullyMessage = translations['company.logoImageUploadedSuccessfullyMessage'];
        this.logoImageIsDeletedMessage = translations['company.logoImageIsDeletedMessage'];
        this.backgroundImageUploadedSuccessfullyMessage = translations['company.backgroundImageUploadedSuccessfullyMessage'];
        this.backgroundImageIsDeletedMessage = translations['company.backgroundImageIsDeletedMessage'];
        this.imageUrlCopiedToClipboardMessage = translations['company.imageUrlCopiedToClipboardMessage'];
      });

    this.eventService.permissionsLoadedEvent.subscribe(() => {
      this.setActiveTabSheet();
    });
  }

  initializeObject(): void {
    if (this.currentObject == null) {
      this.currentObject = new Company();
      this.currentPageStyle = new PageStyle();
    } else {
      this.persistedSlug = this.currentObject.Slug;

      if (this.currentObject.PageStyles.length > 0) {
        this.currentPageStyle = this.currentObject.PageStyles[0];
      } else {
        this.currentPageStyle = new PageStyle(this.currentObject.CompanyId);
      }

      if (!this.currentObject.Slug && this.currentObject.Name) {
        this.currentObject.SlugSuggestion = Utils.generateSlug(this.currentObject.Name, Constants.CompanyMaxSlugLength);
      }

      if (!this.currentObject.AutoCreditUpdateAmount) {
        this.currentObject.AutoCreditUpdateAmount = 5.00;
      }

      if (!this.currentObject.AutoCreditUpdateThreshold) {
        this.currentObject.AutoCreditUpdateThreshold = 1.00;
      }
    }

    super.initializeObject();
  }

  setActiveTabSheet(): void {
    if (this.hasCompanyDetailsViewPermission) {
      setTimeout(() => {
        this.companyDetailsLink.nativeElement.click();
      }, 300);
    } else {
      setTimeout(() => {
        this.companyBanksLink.nativeElement.click();
      }, 300);
    }
  }

  onNameChange(): void {
    if (!this.currentObject.Slug) {
      this.currentObject.SlugSuggestion = Utils.generateSlug(this.currentObject.Name, Constants.CompanyMaxSlugLength);
    }
  }

  onSlugChange(newValue: string): void {
    if (!newValue) {
      this.currentObject.Slug = this.currentObject.SlugSuggestion || '';
    } else {
      this.currentObject.Slug = newValue;
    }
  }

  handleNumberOfDaysChanged(daysOption: DaysOption): void {
    if (daysOption) {
      this.currentObject.PaymentRequestNumberOfDaysValid = daysOption.Value;
    } else {
      this.currentObject.PaymentRequestNumberOfDaysValid = undefined;
    }
  }

  openOnboardingUrl(): void {
    window.open(this.currentObject.IbanXSOnboardingUrl, '_blank');
  }

  startIbanXSOnboarding(): void {
    this.companyService.startIbanXSOnboarding(
      this.currentObject.CompanyId,
      this.currentObject,
      (ibanXSOnboardingUrl: string) => this.currentObject.IbanXSOnboardingUrl = ibanXSOnboardingUrl,
      this.onboardingUrlGeneratedSuccessfullyMessage
    );
    this.refreshNeeded = true;
  }

  saveLogoImage(files: FileList): void {
    const fileToUpload: File = files.item(0);
    this.imageRequestInFlight = true;
    this.pageStyleService.saveLogoImage(
      this.currentObject.CompanyId,
      fileToUpload,
      () => this.updateLogoImage(fileToUpload.name),
      format(this.logoImageUploadedSuccessfullyMessage, fileToUpload.name),
      () => this.imageRequestInFlight = false
    );
  }

  private updateLogoImage(fileName: string): void {
    this.currentPageStyle.LogoImageFileName = fileName;
  }

  deleteLogoImage(): void {
    if (this.currentPageStyle.LogoImageFileName) {
      this.imageRequestInFlight = true;
      this.pageStyleService.deleteLogoImage(
        this.currentObject.CompanyId,
        this.currentPageStyle.LogoImageFileName,
        () => this.clearLogoImage(), format(this.logoImageIsDeletedMessage,
          this.currentPageStyle.LogoImageFileName),
        () => this.imageRequestInFlight = false
      );
    }
  }

  private clearLogoImage(): void {
    this.currentPageStyle.LogoImageFileName = null;
  }

  saveBackgroundImage(files: FileList): void {
    const fileToUpload: File = files.item(0);
    this.imageRequestInFlight = true;
    this.pageStyleService.saveBackgroundImage(
      this.currentObject.CompanyId,
      fileToUpload,
      () => this.updateBackgroundImage(fileToUpload.name),
      format(this.backgroundImageUploadedSuccessfullyMessage, fileToUpload.name),
      () => this.imageRequestInFlight = false
    );
  }

  private updateBackgroundImage(fileName: string): void {
    this.currentPageStyle.BackgroundImageFileName = fileName;
  }

  deleteBackgroundImage(): void {
    if (this.currentPageStyle.BackgroundImageFileName) {
      this.imageRequestInFlight = true;
      this.pageStyleService.deleteBackgroundImage(
        this.currentObject.CompanyId,
        this.currentPageStyle.BackgroundImageFileName,
        () => this.clearBackgroundImage(),
        format(this.backgroundImageIsDeletedMessage, this.currentPageStyle.BackgroundImageFileName),
        () => this.imageRequestInFlight = false
      );
    }
  }

  private clearBackgroundImage(): void {
    this.currentPageStyle.BackgroundImageFileName = null;
  }

  copyLogoImageUrl(event: MouseEvent): void {
    this.copyImageUrl(event, this.logoImageUrl);
  }

  copyBackgroundImageUrl(event: MouseEvent): void {
    this.copyImageUrl(event, this.backgroundImageUrl);
  }

  /**
   * Copy the image URL on a left click. Any other button is left to the browser, so that a middle click
   * on this anchor opens the image in a new tab like it would on any other link.
   */
  private copyImageUrl(event: MouseEvent, imageUrl: string): void {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();

    if (!imageUrl) {
      return;
    }

    Utils.copyToClipBoard(this.toastrService, this.imageUrlCopiedToClipboardMessage, imageUrl);
  }

  onSubmit(form: NgForm): void {
    if (this.moduleSelectionComponent != null) {
      if (this.moduleSelectionComponent.hasInactiveSelectedModules()) {
        return;
      }

      this.applySelectedModulesToCompany();
    }

    this.getPageStyleForCompany();

    if (Utils.isStringNullOrWhiteSpace(this.currentObject.Slug) && !Utils.isStringNullOrWhiteSpace(this.currentObject.SlugSuggestion)) {
      this.currentObject.Slug = this.currentObject.SlugSuggestion;
    }

    if (!this.currentObject.CompanyId) {
      this.companyService.postCompany(
        this.currentObject,
        () => this.handleSubmit(form),
        this.addedSuccessfullyMessage,
        (error: HttpErrorResponse) => this.handleSaveError(error));
    } else {
      this.companyService.putCompany(
        this.currentObject.CompanyId,
        this.currentObject,
        () => this.handleSubmit(form),
        this.updatedSuccessfullyMessage,
        (error: HttpErrorResponse) => this.handleSaveError(error));
    }
  }

  /**
   * Keeps the modal open on a rejected duplicate slug and records which value was refused, so the slug field
   * can say so. Recognised by the error code rather than the message text, which is an English sentence meant
   * for a log and would break this the moment it is reworded.
   */
  handleSaveError(error: HttpErrorResponse): void {
    if (error && error.error && error.error.ErrorCode === Constants.CompanySlugNotUniqueErrorCode) {
      this.rejectedSlug = error.error.Slug !== undefined ? error.error.Slug : this.currentObject.Slug;
    }
  }

  applySelectedModulesToCompany(): void {
    this.currentObject.CompanyModules.length = 0;
    const selectedModules: Array<ModuleSelection> = this.moduleSelectionComponent.getSelected();

    for (const selectedModule of selectedModules) {
      if (selectedModule.IsChecked) {
        this.currentObject.CompanyModules.push(new CompanyModule(this.currentObject.CompanyId, selectedModule));
      }
    }
  }

  getPageStyleForCompany(): void {
    this.currentObject.PageStyles.length = 0;
    this.currentObject.PageStyles.push(this.currentPageStyle);
  }

  closeModal(): void {
    super.closeModal();
    if (this.refreshNeeded) {
      this.afterSubmitEvent.emit();
    }
  }

  handleCustomerOrganizationChanged(organization: CustomerOrganizationBaseModel): void {
    if (organization) {
      this.currentObject.ExactAccountId = organization.Id;
    } else {
      this.currentObject.ExactAccountId = undefined;
    }
  }
}
